import type { AuthUser } from '@/modules/auth/types';
import { useTenant } from '@/providers/TenantProvider';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { cn } from '@/shared/lib/cn';
import { useTranslation } from 'react-i18next';

interface PortalHeaderProps {
  user: AuthUser;
  onMenuClick: () => void;
}

export function PortalHeader({ user, onMenuClick }: PortalHeaderProps) {
  const { t } = useTranslation();
  const { activeTenant } = useTenant();

  const companyLabel =
    activeTenant?.display_name?.trim() ||
    activeTenant?.name?.trim() ||
    user.fullName?.trim() ||
    null;

  return (
    <header className="portal-header">
      <div className="flex shrink-0 items-center lg:hidden">
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={t('nav.openMenu')}
          onClick={onMenuClick}
        >
          <span className="flex flex-col gap-1" aria-hidden>
            <span className="block h-0.5 w-5 rounded-full bg-current" />
            <span className="block h-0.5 w-5 rounded-full bg-current" />
            <span className="block h-0.5 w-5 rounded-full bg-current" />
          </span>
        </button>
      </div>

      <div className="hidden min-w-0 flex-1 flex-col justify-center lg:flex">
        {companyLabel ? (
          <span className="truncate text-sm font-medium text-foreground">{companyLabel}</span>
        ) : null}
      </div>

      <div className="ms-auto flex min-w-0 items-center justify-end gap-1.5 sm:gap-2 md:gap-3">
        <LanguageSwitcher className="shrink-0" />
        {companyLabel ? (
          <span className="truncate text-sm font-medium text-foreground max-w-[8rem] sm:max-w-[10rem] lg:hidden">
            {companyLabel}
          </span>
        ) : null}
        <span className="hidden max-w-[10rem] truncate text-sm text-muted-foreground sm:inline xl:max-w-[12rem]">
          {user.email}
        </span>
        <a
          href="/auth/logout"
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-md text-primary hover:bg-muted hover:text-primary',
            'h-8 w-8 sm:h-auto sm:w-auto sm:px-0 sm:hover:bg-transparent sm:hover:underline',
            'text-sm font-medium',
          )}
          aria-label={t('auth.logout.signOut')}
        >
          <svg
            className="h-4 w-4 sm:hidden"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" x2="9" y1="12" y2="12" />
          </svg>
          <span className="hidden sm:inline">{t('auth.logout.signOut')}</span>
        </a>
      </div>
    </header>
  );
}
